import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronRight, ChevronLeft, CheckCircle2, Brain, Target, 
  Sparkles, Trophy, Loader2, BookOpen, Dumbbell, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

// Assessment Questions - 15 high-impact questions
const QUESTIONS = [
  // Academic Confidence (1-5)
  {
    id: 1,
    category: 'Academic Confidence',
    icon: BookOpen,
    question: "How confident are you in your current GPA trajectory?",
    options: [
      { value: 1, label: "Struggling significantly" },
      { value: 2, label: "Below where I want to be" },
      { value: 3, label: "Average but not improving" },
      { value: 4, label: "Good and getting better" },
      { value: 5, label: "Excellent - crushing it" }
    ]
  },
  {
    id: 2,
    category: 'Academic Confidence',
    icon: BookOpen,
    question: "How often do you complete homework/assignments on time?",
    options: [
      { value: 1, label: "Rarely - I'm always behind" },
      { value: 2, label: "Sometimes - it depends" },
      { value: 3, label: "About half the time" },
      { value: 4, label: "Usually on time" },
      { value: 5, label: "Always - no excuses" }
    ]
  },
  {
    id: 3,
    category: 'Academic Confidence',
    icon: BookOpen,
    question: "How prepared do you feel for your next level (college/career)?",
    options: [
      { value: 1, label: "Not prepared at all" },
      { value: 2, label: "Slightly prepared" },
      { value: 3, label: "Somewhat prepared" },
      { value: 4, label: "Well prepared" },
      { value: 5, label: "Extremely prepared" }
    ]
  },
  {
    id: 4,
    category: 'Academic Confidence',
    icon: BookOpen,
    question: "How would you rate your study habits?",
    options: [
      { value: 1, label: "No real system" },
      { value: 2, label: "Inconsistent at best" },
      { value: 3, label: "Decent but room to grow" },
      { value: 4, label: "Strong and consistent" },
      { value: 5, label: "Elite - highly optimized" }
    ]
  },
  {
    id: 5,
    category: 'Academic Confidence',
    icon: BookOpen,
    question: "How well do you balance academics with athletics?",
    options: [
      { value: 1, label: "Sports dominate everything" },
      { value: 2, label: "Academics suffer during season" },
      { value: 3, label: "Managing but stressed" },
      { value: 4, label: "Good balance most days" },
      { value: 5, label: "Perfect harmony achieved" }
    ]
  },
  // Sports IQ / Tactical Awareness (6-10)
  {
    id: 6,
    category: 'Sports IQ',
    icon: Dumbbell,
    question: "How well do you understand your sport's strategy beyond your position?",
    options: [
      { value: 1, label: "Just know my role" },
      { value: 2, label: "Basic team understanding" },
      { value: 3, label: "Above average awareness" },
      { value: 4, label: "Can coach my teammates" },
      { value: 5, label: "Film room genius" }
    ]
  },
  {
    id: 7,
    category: 'Sports IQ',
    icon: Dumbbell,
    question: "How would you rate your leadership presence on the field/court?",
    options: [
      { value: 1, label: "Just following along" },
      { value: 2, label: "Quiet contributor" },
      { value: 3, label: "Vocal when needed" },
      { value: 4, label: "Natural leader" },
      { value: 5, label: "Captain material" }
    ]
  },
  {
    id: 8,
    category: 'Sports IQ',
    icon: Dumbbell,
    question: "How often do you study game film or analyze your performance?",
    options: [
      { value: 1, label: "Never" },
      { value: 2, label: "Only when coach makes us" },
      { value: 3, label: "Occasionally on my own" },
      { value: 4, label: "Weekly habit" },
      { value: 5, label: "Daily - obsessed with improvement" }
    ]
  },
  {
    id: 9,
    category: 'Sports IQ',
    icon: Dumbbell,
    question: "How do you respond to coaching criticism?",
    options: [
      { value: 1, label: "Take it personally, shut down" },
      { value: 2, label: "Frustrated but try to improve" },
      { value: 3, label: "Accept it, move on" },
      { value: 4, label: "Welcome it, apply immediately" },
      { value: 5, label: "Seek it out proactively" }
    ]
  },
  {
    id: 10,
    category: 'Sports IQ',
    icon: Dumbbell,
    question: "How prepared are you for the recruiting process?",
    options: [
      { value: 1, label: "Haven't thought about it" },
      { value: 2, label: "Know basics only" },
      { value: 3, label: "Started researching" },
      { value: 4, label: "Active outreach to coaches" },
      { value: 5, label: "Full recruiting plan in motion" }
    ]
  },
  // Brand Awareness (11-15)
  {
    id: 11,
    category: 'Brand Awareness',
    icon: Palette,
    question: "Do you have a personal mission statement or motto?",
    options: [
      { value: 1, label: "Never thought about it" },
      { value: 2, label: "Have vague ideas" },
      { value: 3, label: "Working on defining it" },
      { value: 4, label: "Clear personal mission" },
      { value: 5, label: "Live it daily, share it publicly" }
    ]
  },
  {
    id: 12,
    category: 'Brand Awareness',
    icon: Palette,
    question: "How would you describe your social media presence?",
    options: [
      { value: 1, label: "Random or non-existent" },
      { value: 2, label: "Mostly personal/unfiltered" },
      { value: 3, label: "Starting to be strategic" },
      { value: 4, label: "Curated athlete brand" },
      { value: 5, label: "Professional presence, engagement strategy" }
    ]
  },
  {
    id: 13,
    category: 'Brand Awareness',
    icon: Palette,
    question: "Do you have a personal logo, brand colors, or visual identity?",
    options: [
      { value: 1, label: "No, never considered it" },
      { value: 2, label: "Thought about it" },
      { value: 3, label: "Have some ideas sketched" },
      { value: 4, label: "Basic brand elements ready" },
      { value: 5, label: "Full visual brand system" }
    ]
  },
  {
    id: 14,
    category: 'Brand Awareness',
    icon: Palette,
    question: "Can you articulate what makes you unique as an athlete?",
    options: [
      { value: 1, label: "No idea" },
      { value: 2, label: "Struggling to define it" },
      { value: 3, label: "Know some strengths" },
      { value: 4, label: "Clear value proposition" },
      { value: 5, label: "Elevator pitch ready" }
    ]
  },
  {
    id: 15,
    category: 'Brand Awareness',
    icon: Palette,
    question: "How often do you document your athletic journey?",
    options: [
      { value: 1, label: "Never" },
      { value: 2, label: "Occasionally when reminded" },
      { value: 3, label: "When something big happens" },
      { value: 4, label: "Regular highlights/updates" },
      { value: 5, label: "Consistent content strategy" }
    ]
  }
];

// Determine Legacy Major based on category scores
const determineMajor = (categoryScores) => {
  const { academic, sports, brand } = categoryScores;
  
  // The Architect - Strongest in Academic + Brand
  if (academic >= sports && brand >= sports) {
    return {
      name: "The Architect",
      description: "You build foundations. Your academic drive and brand awareness create lasting structures for success.",
      color: "#3B82F6", // Blue
      icon: "🏗️"
    };
  }
  
  // The Tactician - Strongest in Sports IQ + Academic
  if (sports >= brand && academic >= brand) {
    return {
      name: "The Tactician",
      description: "You see the game 3 moves ahead. Your sports IQ combined with academic discipline makes you a strategic powerhouse.",
      color: "#10B981", // Green
      icon: "♟️"
    };
  }
  
  // The Powerhouse - Strongest in Sports IQ + Brand
  if (sports >= academic && brand >= academic) {
    return {
      name: "The Powerhouse",
      description: "You dominate presence. Your athletic excellence and personal brand create undeniable impact.",
      color: "#F97316", // Orange
      icon: "⚡"
    };
  }
  
  // Default to balanced
  return {
    name: "The Renaissance",
    description: "You're well-rounded across all dimensions. A true scholar-athlete in the making.",
    color: "#8B5CF6", // Purple
    icon: "🎯"
  };
};

const LegacyDiagnostic = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, questions, calculating, complete
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '', sport: '', grade: '' });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleStartAssessment = (e) => {
    e.preventDefault();
    if (!userInfo.name || !userInfo.email || !userInfo.sport || !userInfo.grade) {
      toast.error('Please fill in all fields');
      return;
    }
    setStep('questions');
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance to next question after a brief delay
    setTimeout(() => {
      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    // Check all questions answered
    if (Object.keys(answers).length < QUESTIONS.length) {
      toast.error('Please answer all questions');
      return;
    }

    setStep('calculating');
    setLoading(true);

    try {
      // Calculate scores
      const answersArray = QUESTIONS.map(q => answers[q.id]);
      const totalScore = answersArray.reduce((sum, val) => sum + val, 0);
      const maxScore = QUESTIONS.length * 5; // 75 max
      const percentageScore = Math.round((totalScore / maxScore) * 100);
      
      // Calculate category scores
      const academicScore = answersArray.slice(0, 5).reduce((sum, val) => sum + val, 0);
      const sportsScore = answersArray.slice(5, 10).reduce((sum, val) => sum + val, 0);
      const brandScore = answersArray.slice(10, 15).reduce((sum, val) => sum + val, 0);
      
      // Determine major
      const major = determineMajor({
        academic: academicScore,
        sports: sportsScore,
        brand: brandScore
      });
      
      // Calculate recruitability vs potential
      const recruitability = Math.round((totalScore / maxScore) * 100);
      const potential = Math.min(100, recruitability + 25); // Potential is always higher
      
      // Submit to backend
      const response = await api.post('/academy/legacy-diagnostic/submit', {
        name: userInfo.name,
        email: userInfo.email,
        sport: userInfo.sport,
        grade: userInfo.grade,
        answers: answersArray,
        total_score: percentageScore,
        category_scores: {
          academic: academicScore,
          sports: sportsScore,
          brand: brandScore
        },
        major: major.name
      });

      // Store results
      const resultsData = {
        submissionId: response.data.submission_id,
        score: percentageScore,
        major,
        recruitability,
        potential,
        categoryScores: {
          academic: Math.round((academicScore / 25) * 100),
          sports: Math.round((sportsScore / 25) * 100),
          brand: Math.round((brandScore / 25) * 100)
        },
        userInfo
      };
      
      setResults(resultsData);
      
      // Brief pause for dramatic effect
      setTimeout(() => {
        setStep('complete');
        // Navigate to Performer's Audit results page with data
        navigate('/academy/performers-audit', { 
          state: resultsData 
        });
      }, 2000);

    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Failed to submit assessment. Please try again.');
      setStep('questions');
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
  const currentQ = QUESTIONS[currentQuestion];
  const CategoryIcon = currentQ?.icon || BookOpen;

  // Intro Screen
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-black text-white pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* SAA Logo */}
          <div className="text-center mb-8">
            <img 
              src="/images/scholar-athlete-logo-new.png" 
              alt="Scholar Athlete Academy" 
              className="mx-auto h-32 md:h-40 w-auto"
              style={{ filter: 'drop-shadow(0 0 20px rgba(249,115,22,0.3))' }}
            />
          </div>

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
              <Brain className="w-4 h-4 text-orange-500" />
              <span className="text-orange-500 text-sm font-bold tracking-wider">THE COMBINE DIAGNOSTIC</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              DISCOVER YOUR <span className="text-orange-500">POWER SCORE</span>
            </h1>
            <p className="text-gray-400 text-lg">
              15 questions. 5 minutes. One score that defines your starting line.
            </p>
          </div>

          {/* What You'll Learn */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-white mb-4">This diagnostic will reveal:</h3>
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Academic Confidence</p>
                  <p className="text-gray-500 text-sm">Your GPA trajectory and study habits</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Dumbbell className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Sports IQ</p>
                  <p className="text-gray-500 text-sm">Tactical awareness and leadership</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Palette className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium text-white">Brand Awareness</p>
                  <p className="text-gray-500 text-sm">Personal mission and public presence</p>
                </div>
              </div>
            </div>
          </div>

          {/* User Info Form */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <form onSubmit={handleStartAssessment} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                    <Input
                      id="name"
                      value={userInfo.name}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-gray-800 border-gray-700 text-white mt-1"
                      placeholder="Your name"
                      required
                      data-testid="diagnostic-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userInfo.email}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-gray-800 border-gray-700 text-white mt-1"
                      placeholder="your@email.com"
                      required
                      data-testid="diagnostic-email-input"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sport" className="text-gray-300">Primary Sport</Label>
                    <Input
                      id="sport"
                      value={userInfo.sport}
                      onChange={(e) => setUserInfo(prev => ({ ...prev, sport: e.target.value }))}
                      className="bg-gray-800 border-gray-700 text-white mt-1"
                      placeholder="e.g., Basketball, Football"
                      required
                      data-testid="diagnostic-sport-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="grade" className="text-gray-300">Current Grade</Label>
                    <select
                      id="grade"
                      value={userInfo.grade}
                      onChange={(e) => {
                        const selectedGrade = e.target.value;
                        setUserInfo(prev => ({ ...prev, grade: selectedGrade }));
                        // If Parent/Executive selected, redirect to Parent Diagnostic
                        if (selectedGrade === 'parent') {
                          navigate('/academy/parent-diagnostic');
                        }
                      }}
                      className="w-full bg-gray-800 border border-gray-700 text-white mt-1 rounded-md px-3 py-2"
                      required
                      data-testid="diagnostic-grade-select"
                    >
                      <option value="">Select grade</option>
                      <option value="6th">6th Grade</option>
                      <option value="7th">7th Grade</option>
                      <option value="8th">8th Grade</option>
                      <option value="highschool">High School (Freshman-Senior)</option>
                      <option value="college">College</option>
                      <option value="parent">Parent / Executive</option>
                    </select>
                  </div>
                </div>
                
                {/* Parent Email - for notifications */}
                <div>
                  <Label htmlFor="parentEmail" className="text-gray-300">
                    Parent/Guardian Email <span className="text-gray-500 text-sm">(Optional - for result notifications)</span>
                  </Label>
                  <Input
                    id="parentEmail"
                    type="email"
                    value={userInfo.parentEmail || ''}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, parentEmail: e.target.value }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="parent@email.com"
                    data-testid="diagnostic-parent-email-input"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6 text-lg mt-4"
                  data-testid="start-diagnostic-btn"
                >
                  BEGIN DIAGNOSTIC
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-gray-600 text-sm mt-6">
            Your results are private and will be used to personalize your Academy experience.
          </p>
        </div>
      </div>
    );
  }

  // Questions Screen
  if (step === 'questions') {
    return (
      <div className="min-h-screen bg-black text-white pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Question {currentQuestion + 1} of {QUESTIONS.length}</span>
              <span className="text-sm text-orange-500 font-bold">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Category Badge */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <CategoryIcon className="w-4 h-4 text-orange-500" />
            </div>
            <span className="text-orange-500 font-bold text-sm tracking-wider uppercase">
              {currentQ.category}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-white">
            {currentQ.question}
          </h2>

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQ.id, option.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  answers[currentQ.id] === option.value
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                }`}
                data-testid={`option-${option.value}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    answers[currentQ.id] === option.value
                      ? 'border-orange-500 bg-orange-500'
                      : 'border-gray-600'
                  }`}>
                    {answers[currentQ.id] === option.value && (
                      <CheckCircle2 className="w-4 h-4 text-black" />
                    )}
                  </div>
                  <span className={`text-base ${
                    answers[currentQ.id] === option.value ? 'text-white' : 'text-gray-300'
                  }`}>
                    {option.label}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-4">
            <Button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentQuestion === QUESTIONS.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={Object.keys(answers).length < QUESTIONS.length}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                data-testid="submit-diagnostic-btn"
              >
                Get My Results
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={!answers[currentQ.id]}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                data-testid="next-question-btn"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Calculating Screen
  if (step === 'calculating') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-orange-500/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <Brain className="absolute inset-0 m-auto w-10 h-10 text-orange-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Analyzing Your Legacy Profile...</h2>
          <p className="text-gray-500">Calculating your score and determining your major</p>
        </div>
      </div>
    );
  }

  return null;
};

export default LegacyDiagnostic;
